import { supabase } from '../lib/supabase';

// Generate a unique identifier for anonymous users (stored in localStorage)
const getUserIdentifier = () => {
  let identifier = localStorage.getItem('user_identifier');
  if (!identifier) {
    identifier = `anon_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    localStorage.setItem('user_identifier', identifier);
  }
  return identifier;
};

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  // Check PostgreSQL error code class (first 2 characters)
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    
    // Class 42 = Schema errors (syntax, undefined table/column, etc.)
    if (errorClass === '42') {
      return true;
    }
    
    // Class 23 = Data errors (constraints, validation) - NOT schema errors
    if (errorClass === '23') {
      return false;
    }
    
    // Class 08 = Connection errors
    if (errorClass === '08') {
      return true;
    }
  }
  
  // Check for actual schema error patterns (more specific)
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation "[^"]+" does not exist/i,
      /column "[^"]+" does not exist/i,
      /function "[^"]+" does not exist/i,
      /syntax error at or near/i,
      /type "[^"]+" does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert database snake_case to camelCase
const toCamelCase = (post) => {
  if (!post) return null;
  return {
    id: post?.id,
    content: post?.content,
    isHidden: post?.is_hidden,
    createdAt: post?.created_at,
    updatedAt: post?.updated_at,
    reactions: {
      relate: post?.relate_count || 0,
      correct: post?.correct_count || 0,
      notBest: post?.not_best_count || 0
    }
  };
};

// Create a new post
export const createPost = async (content) => {
  try {
    const { data, error } = await supabase?.from('posts')?.insert({ content })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

// Fetch all posts (not hidden)
export const fetchPosts = async (limit = 10, offset = 0) => {
  try {
    const { data, error, count } = await supabase?.from('posts')?.select('*', { count: 'exact' })?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return { posts: [], total: 0 };
    }

    return {
      posts: data?.map(toCamelCase) || [],
      total: count || 0
    };
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
};

// Get user's reactions for posts
export const getUserReactions = async (postIds) => {
  try {
    const userIdentifier = getUserIdentifier();

    const { data, error } = await supabase
      ?.from('post_reactions')
      ?.select('post_id, reaction_type')
      ?.eq('user_identifier', userIdentifier)
      ?.in('post_id', postIds);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return {};
    }

    // Convert to map: { postId: reactionType }
    const reactionsMap = {};
    data?.forEach(reaction => {
      reactionsMap[reaction?.post_id] = reaction?.reaction_type;
    });

    return reactionsMap;
  } catch (error) {
    console.error('Error fetching user reactions:', error);
    return {};
  }
};

// Add a reaction to a post (or switch to a different reaction)
export const addReaction = async (postId, reactionType) => {
  try {
    const userIdentifier = getUserIdentifier();

    // First, check if user already has a reaction on this post
    const { data: existingReaction } = await supabase
      ?.from('post_reactions')
      ?.select('id, reaction_type')
      ?.eq('post_id', postId)
      ?.eq('user_identifier', userIdentifier)
      ?.single();

    // If user already reacted with the same type, do nothing
    if (existingReaction && existingReaction?.reaction_type === reactionType) {
      console.log('User already reacted with this type');
      return null;
    }

    // If user has a different reaction, delete it first
    if (existingReaction) {
      const { error: deleteError } = await supabase
        ?.from('post_reactions')
        ?.delete()
        ?.eq('id', existingReaction?.id);

      if (deleteError) {
        console.error('Error deleting old reaction:', deleteError?.message);
        throw deleteError;
      }
    }

    // Insert the new reaction
    const { data, error } = await supabase
      ?.from('post_reactions')
      ?.insert({
        post_id: postId,
        user_identifier: userIdentifier,
        reaction_type: reactionType
      })
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return null;
    }

    return { 
      ...data, 
      previousReaction: existingReaction?.reaction_type 
    };
  } catch (error) {
    console.error('Error adding reaction:', error);
    throw error;
  }
};

// Flag a post as not anonymous
export const flagPost = async (postId) => {
  try {
    const userIdentifier = getUserIdentifier();

    // Add flag record
    const { error: flagError } = await supabase?.from('post_flags')?.insert({
        post_id: postId,
        user_identifier: userIdentifier
      });

    if (flagError) {
      if (isSchemaError(flagError)) {
        console.error('Schema error:', flagError?.message);
        throw flagError;
      }
      // Duplicate flag - handle gracefully
      if (flagError?.code === '23505') {
        console.log('User already flagged this post');
      } else {
        console.error('Data error:', flagError?.message);
      }
    }

    // Hide the post
    const { data, error } = await supabase?.from('posts')?.update({ is_hidden: true })?.eq('id', postId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error flagging post:', error);
    throw error;
  }
};

// Update a post (for edit functionality)
export const updatePost = async (postId, content) => {
  try {
    const { data, error } = await supabase?.from('posts')?.update({ content, is_hidden: false, updated_at: new Date()?.toISOString() })?.eq('id', postId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating post:', error);
    throw error;
  }
};

// Delete a post
export const deletePost = async (postId) => {
  try {
    const { error } = await supabase?.from('posts')?.delete()?.eq('id', postId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.error('Data error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

// Get time ago string
export const getTimeAgo = (timestamp) => {
  const now = new Date();
  const postDate = new Date(timestamp);
  const diffInSeconds = Math.floor((now - postDate) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 172800) return 'Yesterday';
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  return 'This week';
};